import React, { useState, useEffect } from 'react';
import { AIQuotationTemplate, AITemplateVariable } from '../../types/aiQuotation';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
  DocumentTextIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  EyeIcon,
  CheckIcon,
  XMarkIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import { Card } from '../common/Card';
import Modal from '../common/Modal';
import { Input } from '../common/Input';
import { Button } from '../common/Button';

interface TemplateSelectorProps {
  templates: AIQuotationTemplate[];
  selectedTemplate?: AIQuotationTemplate | null;
  onTemplateSelect: (template: AIQuotationTemplate | null) => void;
  onTemplateCreate?: (template: Omit<AIQuotationTemplate, 'id' | 'createdAt' | 'updatedAt' | 'usageCount'>) => void;
  onTemplateUpdate?: (id: string, template: Partial<AIQuotationTemplate>) => void;
  onTemplateDelete?: (id: string) => void;
  loading?: boolean;
  className?: string;
}

const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  templates,
  selectedTemplate,
  onTemplateSelect,
  onTemplateCreate,
  onTemplateUpdate,
  onTemplateDelete,
  loading = false,
  className = ""
}) => {
  const { t } = useLanguage();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<AIQuotationTemplate | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<AIQuotationTemplate | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const categories = [
    { value: 'all', label: 'Összes sablon' },
    { value: 'general', label: 'Általános' },
    { value: 'service', label: 'Szolgáltatás' },
    { value: 'product', label: 'Termék' },
    { value: 'consulting', label: 'Tanácsadás' },
    { value: 'maintenance', label: 'Karbantartás' },
    { value: 'custom', label: 'Egyedi' }
  ];

  const filteredTemplates = templates.filter(template => 
    selectedCategory === 'all' || template.category === selectedCategory
  );

  const getCategoryLabel = (category: string) => {
    return categories.find(cat => cat.value === category)?.label || category;
  };

  const getCategoryColor = (category: string) => {
    const colors = {
      general: 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-700',
      service: 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700',
      product: 'bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-300 border-purple-200 dark:border-purple-700',
      consulting: 'bg-orange-100 dark:bg-orange-900/20 text-orange-800 dark:text-orange-300 border-orange-200 dark:border-orange-700',
      maintenance: 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-700',
      custom: 'bg-gray-100 dark:bg-gray-900/20 text-gray-800 dark:text-gray-300 border-gray-200 dark:border-gray-700'
    };
    return colors[category as keyof typeof colors] || colors.custom;
  };

  return (
    <div className={className}>
      {/* Header with category filter and create button */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex flex-wrap gap-2">
          {categories.map((category) => (
            <button
              key={category.value}
              onClick={() => setSelectedCategory(category.value)}
              className={`px-3 py-1.5 text-sm font-medium rounded-full border transition-colors ${
                selectedCategory === category.value
                  ? 'bg-primary-100 dark:bg-primary-900/20 text-primary-800 dark:text-primary-300 border-primary-300 dark:border-primary-700'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              {category.label}
            </button>
          ))}
        </div>

        {onTemplateCreate && (
          <Button
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center space-x-2"
          >
            <PlusIcon className="h-4 w-4" />
            <span>Új sablon</span>
          </Button>
        )}
      </div>

      {/* Templates grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <div className="p-4">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
              </div>
            </Card>
          ))}
        </div>
      ) : filteredTemplates.length === 0 ? (
        <Card className="text-center py-12">
          <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            Nincsenek sablonok
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {selectedCategory === 'all' 
              ? 'Még nincsenek létrehozott sablonok.'
              : `Nincsenek sablonok a "${getCategoryLabel(selectedCategory)}" kategóriában.`
            }
          </p>
          {onTemplateCreate && (
            <Button onClick={() => setIsCreateModalOpen(true)}>
              <PlusIcon className="h-4 w-4 mr-2" />
              Első sablon létrehozása
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map((template) => (
            <div
              key={template.id} 
              className={`cursor-pointer transition-all hover:shadow-md ${
                selectedTemplate?.id === template.id 
                  ? 'ring-2 ring-primary-500 dark:ring-primary-400' 
                  : ''
              }`}
              onClick={() => onTemplateSelect(template)}
            >
              <Card>
              <div className="p-4">
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {template.name}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                      {template.description}
                    </p>
                  </div>
                  {selectedTemplate?.id === template.id && (
                    <CheckIcon className="h-5 w-5 text-primary-600 dark:text-primary-400 ml-2 flex-shrink-0" />
                  )}
                </div>

                {/* Category badge */}
                <div className="flex items-center justify-between mb-3">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getCategoryColor(template.category)}`}>
                    <TagIcon className="h-3 w-3 mr-1" />
                    {getCategoryLabel(template.category)}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {template.usageCount} használat
                  </span>
                </div>

                {/* Variables count */}
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                  {template.variables.length} változó
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewTemplate(template);
                    }}
                    className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    title="Előnézet"
                  >
                    <EyeIcon className="h-4 w-4" />
                  </button>
                  
                  {onTemplateUpdate && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingTemplate(template);
                      }}
                      className="p-1.5 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      title="Szerkesztés"
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                  )}
                  
                  {onTemplateDelete && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`Biztosan törli a "${template.name}" sablont?`)) {
                          onTemplateDelete(template.id);
                        }
                      }}
                      className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      title="Törlés"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
              </Card>
            </div>
          ))}
        </div>
      )}

      {/* Clear selection button */}
      {selectedTemplate && (
        <div className="mt-4 text-center">
          <Button
            variant="outline"
            onClick={() => onTemplateSelect(null)}
            className="inline-flex items-center space-x-2"
          >
            <XMarkIcon className="h-4 w-4" />
            <span>Sablon törlése</span>
          </Button>
        </div>
      )}

      {/* Template preview modal */}
      <Modal
        isOpen={!!previewTemplate}
        onClose={() => setPreviewTemplate(null)}
        title={`Sablon előnézet: ${previewTemplate?.name}`}
        size="4xl"
      >
        {previewTemplate && (
          <div className="p-6 space-y-6">
            {/* Template info */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Név
                  </label>
                  <p className="text-sm text-gray-900 dark:text-gray-100">{previewTemplate.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Kategória
                  </label>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getCategoryColor(previewTemplate.category)}`}>
                    {getCategoryLabel(previewTemplate.category)}
                  </span>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Leírás
                  </label>
                  <p className="text-sm text-gray-900 dark:text-gray-100">{previewTemplate.description}</p>
                </div>
              </div>
            </div>

            {/* Variables */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                Változók ({previewTemplate.variables.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {previewTemplate.variables.map((variable, index) => (
                  <div key={index} className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {variable.label}
                      </span>
                      {variable.required && (
                        <span className="text-xs text-red-600 dark:text-red-400">Kötelező</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                      <p>Kulcs: <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">{variable.key}</code></p>
                      <p>Típus: {variable.type}</p>
                      {variable.defaultValue && <p>Alapértelmezett: {variable.defaultValue}</p>}
                      {variable.description && <p>Leírás: {variable.description}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Template content */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                Sablon tartalom
              </h3>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                <pre className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap overflow-x-auto">
                  {previewTemplate.content}
                </pre>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TemplateSelector;
